import { useState } from 'react';
import TurnstileWidget from '../common/TurnstileWidget';
import { useQuoteForm } from '../../hooks/useQuoteForm';
import '../../styles/pages/SitePages.css';

const contactDetails = [
  {
    title: 'Call us',
    text: '+1 (770) 590-1666',
    href: 'tel:7705901666',
    action: 'call',
  },
  {
    title: 'Text us',
    text: '+1 (404) 703-3838',
    href: 'sms:+14047033838',
    action: 'text',
  },
  {
    title: 'Email us',
    text: 'sonnyblindpromo@gmail.com',
    action: 'copy',
  },
  {
    title: 'Visit us',
    text: '1680 Roberts Blvd NW Ste 403, Kennesaw, GA 30144',
    href: 'https://www.google.com/maps/place/1680+Roberts+Blvd+%23403,+Kennesaw,+GA+30144/@34.0047566,-84.5979895,17z/data=!3m1!4b1!4m5!3m4!1s0x88f5156f7c9e03cd:0xd95e69e2c2c31e24!8m2!3d34.0047566!4d-84.5954146?entry=ttu&g_ep=EgoyMDI2MDMwOS4wIKXMDSoASAFQAw%3D%3D',
    action: 'visit',
  },
];

function PageHero({ eyebrow, title, description, children }) {
  return (
    <section className="route-hero">
      <div className="container route-hero-panel">
        <p className="route-eyebrow">{eyebrow}</p>
        <h1>{title}</h1>
        <p className="route-description">{description}</p>
        {children ? <div className="route-actions">{children}</div> : null}
      </div>
    </section>
  );
}

function ContactPage() {
  const [copiedEmail, setCopiedEmail] = useState(false);
  const {
    formData,
    handleInputChange,
    handleSubmit,
    isSubmitting,
    submitStatus,
    canSubmit,
    turnstileEnabled,
    turnstileProps,
  } = useQuoteForm({
    successMessage: 'Thank you for reaching out. We will get back to you soon.',
  });

  const handleCopyEmail = async () => {
    try {
      await navigator.clipboard.writeText('sonnyblindpromo@gmail.com');
      setCopiedEmail(true);
      window.setTimeout(() => setCopiedEmail(false), 1600);
    } catch {
      setCopiedEmail(false);
    }
  };

  const scrollToQuoteSection = () => {
    const quoteSection = document.getElementById('request-a-quote');

    if (quoteSection) {
      quoteSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <>
      <PageHero
        eyebrow="Get in touch"
        title="Contact Sonny Blinds"
        description="Send a message, call, or text to ask about products, measurements, installation, or a free quote."
      >
        <a href="tel:7705901666" className="btn btn-primary">Call now</a>
        <button type="button" className="btn btn-primary" onClick={scrollToQuoteSection}>Request a Quote</button>
      </PageHero>

      <section className="route-content-section">
        <div className="container">
          <div className="contact-route-grid">
            <aside className="contact-route-card contact-route-details">
              <h2>Contact details</h2>
              <p className="contact-route-intro">
                The fastest way to reach us is by phone or text during business hours.
              </p>

              <div className="contact-route-links">
                {contactDetails.map((item) => (
                  item.action === 'copy' ? (
                    <button
                      key={item.title}
                      type="button"
                      className="contact-route-link contact-route-link-copy"
                      onClick={handleCopyEmail}
                    >
                      <span>{item.title}</span>
                      <strong>{item.text}</strong>
                      <span className={`contact-route-copy-message ${copiedEmail ? 'is-visible' : ''}`} aria-live="polite">
                        {copiedEmail ? 'Email copied!' : 'Tap anywhere to copy'}
                      </span>
                    </button>
                  ) : (
                    <a
                      key={item.title}
                      href={item.href}
                      className="contact-route-link"
                      target={item.action === 'visit' ? '_blank' : undefined}
                      rel={item.action === 'visit' ? 'noopener noreferrer' : undefined}
                    >
                      <span>{item.title}</span>
                      <strong>{item.text}</strong>
                    </a>
                  )
                ))}
              </div>

              <div className="contact-route-hours">
                <h3>Hours</h3>
                <p>Monday - Friday: 8am - 5pm (By Appointment)</p>
              </div>
            </aside>

            <section id="request-a-quote" className="contact-route-card contact-route-form-card">
              <h2>Request a quote</h2>
              <p className="contact-route-intro">
                Tell us what you need and we will follow up with next steps.
              </p>

              <form className="contact-route-form" onSubmit={handleSubmit}>
                <div className="contact-route-field">
                  <label htmlFor="contact-name">Name *</label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="contact-route-field-group">
                  <div className="contact-route-field">
                    <label htmlFor="contact-email">Email *</label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className="contact-route-field">
                    <label htmlFor="contact-phone">Phone</label>
                    <input
                      id="contact-phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>

                <div className="contact-route-field">
                  <label htmlFor="contact-message">Message *</label>
                  <textarea
                    id="contact-message"
                    name="message"
                    rows="6"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                {turnstileEnabled ? <TurnstileWidget {...turnstileProps} /> : null}

                {submitStatus ? (
                  <p
                    className={`contact-form-status contact-form-status-${submitStatus.type}`}
                    role="alert"
                    aria-live="polite"
                  >
                    {submitStatus.message}
                  </p>
                ) : null}

                <button
                  type="submit"
                  className="btn btn-primary btn-block contact-route-submit"
                  disabled={!canSubmit}
                >
                  {isSubmitting ? 'Sending...' : 'Send message'}
                </button>
              </form>
            </section>
          </div>
        </div>
      </section>
    </>
  );
}

export default ContactPage;